import axios from 'axios'

export interface ProductImage {
  image_id: string
  image_url: string
  is_primary: boolean
  display_order: number
  // Additional fields for blob images
  data_url?: string
  image_data?: string
  file_size?: number
  mime_type?: string
  compressed?: boolean
  compression_ratio?: number
}

export interface Product {
  product_id: string
  name: string
  description: string
  price: number
  currency: string
  currency_code?: string
  stock_quantity: number
  is_available: boolean
  category_id: string
  merchant_id: string
  images: ProductImage[]
  tags: string[]
  dietary_tags?: string[]
  start_date?: string | null
  end_date?: string | null
  created_at: string
  updated_at: string
}

export interface CreateProductDto {
  name: string
  description: string
  price: number
  currency?: string
  stock_quantity: number
  is_available?: boolean
  category_id: string
  merchant_id: string
  images?: string[]
  tags?: string[]
  dietary_tags?: string[]
  start_date?: string | null
  end_date?: string | null
}

export interface UpdateProductDto {
  name?: string
  description?: string
  price?: number
  currency?: string
  stock_quantity?: number
  is_available?: boolean
  category_id?: string
  merchant_id?: string
  images?: string[]
  tags?: string[]
  dietary_tags?: string[]
  start_date?: string | null
  end_date?: string | null
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
}

export interface ProductFilters {
  page?: number
  limit?: number
  category_id?: string
  merchant_id?: string
  is_available?: boolean
  min_price?: number
  max_price?: number
  search?: string
}

// Use proxy configuration from vite.config.ts
const API_BASE_URL = '/api'

const productApi = axios.create({
  baseURL: `${API_BASE_URL}/products`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
productApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
productApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const productService = {
  // Get all products with pagination and filtering
  async getAll(filters: ProductFilters = {}): Promise<PaginatedResponse<Product>> {
    const params = new URLSearchParams()
    
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())
    if (filters.category_id) params.append('category_id', filters.category_id)
    if (filters.merchant_id) params.append('merchant_id', filters.merchant_id)
    if (filters.is_available !== undefined) params.append('is_available', filters.is_available.toString())
    if (filters.min_price) params.append('min_price', filters.min_price.toString())
    if (filters.max_price) params.append('max_price', filters.max_price.toString())
    if (filters.search) params.append('search', filters.search)

    const response = await productApi.get('', { params })
    
    // Transform the response data to ensure currency_code is mapped to currency field
    const apiData = response.data
    if (apiData.data && Array.isArray(apiData.data)) {
      const transformedData = apiData.data.map((productData: any) => {
        // Use currency_code from backend, fallback to currency field, then default to MAD
        const currencyCode = productData.currency_code || productData.currency || 'MAD'
        
        return {
          product_id: productData.product_id,
          name: productData.name || '',
          description: productData.description || '',
          price: productData.price || 0,
          currency: currencyCode || 'MAD', // Use the determined currency code, default to MAD
          currency_code: currencyCode || 'MAD', // Also set currency_code field
          stock_quantity: productData.stock_quantity || 0,
          is_available: productData.is_available ?? true,
          category_id: productData.category_id || '',
          merchant_id: productData.merchant_id || '',
          images: productData.images || [],
          tags: productData.tags || [],
          start_date: productData.start_date ?? null,
          end_date: productData.end_date ?? null,
          created_at: productData.created_at,
          updated_at: productData.updated_at,
          // Include any other fields that might be present
          ...(productData.original_price !== undefined && { original_price: productData.original_price }),
          ...(productData.min_stock_alert !== undefined && { min_stock_alert: productData.min_stock_alert }),
          ...(productData.is_active !== undefined && { is_active: productData.is_active }),
        }
      })
      
      return {
        ...apiData,
        data: transformedData
      }
    }
    
    return apiData
  },

  // Get product by ID
  async getById(id: string): Promise<Product> {
    const response = await productApi.get(`/${id}`)
    const productData = response.data.data || response.data
    // Use currency_code from backend, fallback to currency field, then default to MAD
    const currencyCode = productData.currency_code || productData.currency || 'MAD'
    // Map database fields to Product interface with defaults for missing fields
    return {
      product_id: productData.product_id,
      name: productData.name || '',
      description: productData.description || '',
      price: productData.price || 0,
      currency: currencyCode || 'MAD', // Use the determined currency code, default to MAD
      currency_code: currencyCode || 'MAD', // Also set currency_code field
      stock_quantity: productData.stock_quantity || 0,
      is_available: productData.is_available ?? true,
      category_id: productData.category_id || '',
      merchant_id: productData.merchant_id || '',
      images: productData.images || [], // Default empty array
      tags: productData.tags || [], // Default empty array
      start_date: productData.start_date ?? null,
      end_date: productData.end_date ?? null,
      created_at: productData.created_at,
      updated_at: productData.updated_at
    }
  },

  // Create new product
  async create(productData: CreateProductDto): Promise<Product> {
    // Check if there are images that need to be sent as FormData
    const hasImages = productData.images && productData.images.length > 0;
    
    if (hasImages) {
      // Convert base64 strings to files and send as FormData
      const formData = new FormData();
      
      // Add all non-image fields
      Object.keys(productData).forEach(key => {
        if (key !== 'images') {
          const value = productData[key as keyof CreateProductDto];
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              // For arrays like tags, join them or send as JSON
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value.toString());
            }
          }
        }
      });
      
      // Add images as files
      productData.images?.forEach((image, index) => {
        // Check if image is a base64 string
        if (image.startsWith('data:image')) {
          // Convert base64 to blob
          const base64Data = image.split(',')[1];
          const mimeType = image.match(/data:(.*);base64/)?.[1] || 'image/jpeg';
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: mimeType });
          
          // Create a file from blob
          const fileName = `image_${index}.${mimeType.split('/')[1] || 'jpg'}`;
          const file = new File([blob], fileName, { type: mimeType });
          formData.append('images', file);
        }
      });
      
      // Send as multipart/form-data (productApi baseURL is /api/products)
      const response = await productApi.post('', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': localStorage.getItem('access_token') ? `Bearer ${localStorage.getItem('access_token')}` : ''
        }
      });
      return response.data;
    } else {
      // Send as JSON
      const response = await productApi.post('', productData);
      return response.data;
    }
  },

  // Update product
  async update(id: string, productData: UpdateProductDto): Promise<Product> {
    // Check if there are images that need to be sent as FormData
    const hasImages = productData.images && productData.images.length > 0;
    
    if (hasImages) {
      // Convert base64 strings to files and send as FormData
      const formData = new FormData();
      
      // Add all non-image fields
      Object.keys(productData).forEach(key => {
        if (key !== 'images') {
          const value = productData[key as keyof UpdateProductDto];
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              // For arrays like tags, join them or send as JSON
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value.toString());
            }
          }
        }
      });
      
      // Add images as files
      productData.images?.forEach((image, index) => {
        // Check if image is a base64 string
        if (image.startsWith('data:image')) {
          // Convert base64 to blob
          const base64Data = image.split(',')[1];
          const mimeType = image.match(/data:(.*);base64/)?.[1] || 'image/jpeg';
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: mimeType });
          
          // Create a file from blob
          const fileName = `image_${index}.${mimeType.split('/')[1] || 'jpg'}`;
          const file = new File([blob], fileName, { type: mimeType });
          formData.append('images', file);
        }
      });
      
      // Send as multipart/form-data (productApi baseURL is /api/products)
      const response = await productApi.put(`/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': localStorage.getItem('access_token') ? `Bearer ${localStorage.getItem('access_token')}` : ''
        }
      });
      return response.data;
    } else {
      // Send as JSON
      const response = await productApi.put(`/${id}`, productData);
      return response.data;
    }
  },

  // Delete product
  async delete(id: string): Promise<{ message: string }> {
    const response = await productApi.delete(`/${id}`)
    return response.data
  },

  // Get product statistics
  async getStatistics(): Promise<{
    total: number
    available: number
    unavailable: number
    byCategory: Record<string, number>
  }> {
    try {
      const response = await productApi.get('/statistics')
      return response.data
    } catch (error) {
      // Fallback: calculate from products list
      const products = await this.getAll()
      const total = products.total
      const available = products.data.filter(p => p.is_available).length
      const unavailable = total - available
      const byCategory: Record<string, number> = {}
      products.data.forEach(product => {
        byCategory[product.category_id] = (byCategory[product.category_id] || 0) + 1
      })
      return { total, available, unavailable, byCategory }
    }
  }
}

export default productService
